// #region Imports
import React, { useState, useEffect } from 'react';
import { formatCurrencyBRL, formatDate } from '../../../pages/Produtos/produtos-utils';
import iconSearch from '../../../assets/lupa.svg';
import iconEdit from '../../../assets/edit.svg';
import iconTrash from '../../../assets/trash.svg';
import './ProdutosTable.scss';
// #endregion

export default function ProdutosTable({
  items,
  loading,
  total,
  page,
  pageSize,
  stats,
  filters,
  onPageChange,
  onFiltersChange,
  onCommitFilters,
  onEdit,
  onDelete,
  onCreate,
}) {
  // #region Estado local filtros
  const [draftFilters, setDraftFilters] = useState(filters);
  useEffect(() => { setDraftFilters(filters); }, [filters]);
  // #endregion

  // #region Handlers filtros/paginação
  function updateDraft(patch) { setDraftFilters(prev => ({ ...prev, ...patch })); }
  function applyFilters() { onFiltersChange(draftFilters); onCommitFilters(); }
  function clearFilters() {
    const cleared = { search: '' };
    setDraftFilters(cleared);
    onFiltersChange(cleared);
    onCommitFilters();
  }
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  function jumpToPage(p) { onPageChange(Math.min(Math.max(1, p), totalPages)); }
  // #endregion

  // #region Render helpers
  const renderBody = () => {
    if (loading) {
      return Array.from({ length: 6 }).map((_, i) => (
        <tr key={`sk-${i}`} className="skeleton-row" aria-hidden="true">
          <td colSpan="7">
            <div className="sk-line w40" />
            <div className="sk-line w25" />
          </td>
        </tr>
      ));
    }
    if (items.length === 0) {
      return (
        <>
          <tr><td colSpan="7" className="empty-cell">Nenhum produto encontrado.</td></tr>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={`empty-fill-${i}`} className="row-filler" aria-hidden="true"><td colSpan="7" /></tr>
          ))}
        </>
      );
    }
    return (
      <>
        {items.map(p => (
          <tr key={p.id}>
            <td>
              <div className="cell-main">
                <span className="cell-title">{p.nome}</span>
                {p.descricao && <span className="cell-sub">{p.descricao}</span>}
              </div>
            </td>
            <td>{p.marca}</td>
            <td>{formatCurrencyBRL(p.preco_venda)}</td>
            <td>
              <span className={`tag ${p.estoque_atual <= p.estoque_min ? 'tag-danger-outline' : 'tag-success-outline'}`}>
                {p.estoque_atual} <small>(min: {p.estoque_min})</small>
              </span>
            </td>
            <td>{formatDate(p.validade)}</td>
            <td>{p.lote}</td>
            <td className="actions">
              <button type="button" className="icon-btn" onClick={() => onEdit(p)} aria-label={`Editar ${p.nome}`}>
                <img src={iconEdit} alt="" />
              </button>
              <button type="button" className="icon-btn danger" onClick={() => onDelete(p)} aria-label={`Excluir ${p.nome}`}>
                <img src={iconTrash} alt="" />
              </button>
            </td>
          </tr>
        ))}
        {items.length < 6 && Array.from({ length: 6 - items.length }).map((_, i) => (
          <tr key={`filler-${i}`} className="row-filler" aria-hidden="true"><td colSpan="7" /></tr>
        ))}
      </>
    );
  };
  // #endregion

  // #region Render
  return (
    <div className="produtos-module">
      <div className="produtos-head">
        <h2 className="produtos-title">Produtos</h2>
        <button type="button" onClick={onCreate} className="btn btn-primary">
          <span className="btn-icon">+</span> Novo Produto
        </button>
      </div>
      <div className="kpi-grid" role="region" aria-label="Indicadores de produtos">
        <div className="kpi-card">
          <span className="kpi-label">Total de Produtos</span>
          <span className="kpi-value">{stats.total_produtos}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Estoque Baixo</span>
          <span className="kpi-value kpi-danger">{stats.produtos_baixo_estoque}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Valor Total em Estoque</span>
          <span className="kpi-value kpi-warning">{formatCurrencyBRL(stats.valor_total_estoque)}</span>
        </div>
      </div>
      <div className="filter-panel" role="form" aria-label="Filtros de produtos">
        <div className="filter-header">
          <img src={iconSearch} alt="" className="filter-icon" />
          <span className="filter-title">Filtros</span>
        </div>
        <div className="filter-grid">
          <div className="filter-field">
            <label htmlFor="f-q">Buscar</label>
            <input
              id="f-q"
              placeholder="Nome, marca ou lote..."
              value={draftFilters.search}
              onChange={e => updateDraft({ search: e.target.value })}
              aria-label="Buscar por texto"
              disabled={loading}
            />
          </div>
          <div className="filter-actions">
            <button type="button" className="btn btn-primary" onClick={applyFilters} aria-label="Aplicar filtros" disabled={loading}>Filtrar</button>
            <button type="button" className="btn btn-ghost" onClick={clearFilters} aria-label="Limpar filtros" disabled={loading}>Limpar</button>
          </div>
        </div>
      </div>
      <div className="table-wrapper" role="region" aria-label="Lista de produtos">
        <div className="table-container">
          <table className="ct-table">
            <thead>
              <tr>
                <th>Produto</th>
                <th>Marca</th>
                <th>Preço</th>
                <th>Estoque</th>
                <th>Validade</th>
                <th>Lote</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>{renderBody()}</tbody>
          </table>
        </div>
        <div className="ct-footer" role="navigation" aria-label="Paginação">
          <div className="result-count">Total: {total}</div>
          <div className="pagination">
            <button onClick={() => jumpToPage(1)} disabled={page === 1 || loading} aria-label="Primeira página">&laquo;</button>
            <button onClick={() => jumpToPage(page - 1)} disabled={page === 1 || loading} aria-label="Página anterior">&lsaquo;</button>
            <label className="page-jump">
              <span className="sr-only">Página atual</span>
              <input
                value={String(page)}
                onChange={e => { const v = e.target.value.replace(/\D/g, ''); if (v) jumpToPage(Number(v)); }}
                aria-label="Ir para página"
                inputMode="numeric"
                disabled={loading}
              />
              <span className="total-pages">/ {totalPages}</span>
            </label>
            <button onClick={() => jumpToPage(page + 1)} disabled={page === totalPages || loading} aria-label="Próxima página">&rsaquo;</button>
            <button onClick={() => jumpToPage(totalPages)} disabled={page === totalPages || loading} aria-label="Última página">&raquo;</button>
          </div>
        </div>
      </div>
    </div>
  );
  // #endregion
}

